import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Pagination,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import apiService from '../../services/api';

interface LogEntry {
  timestamp: string;
  level: string;
  source: string;
  message: string;
  details: any;
}

interface LogsResponse {
  logs: LogEntry[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters: {
    level: string | null;
    source: string | null;
    start_date: string | null;
    end_date: string | null;
    search: string | null;
  };
  timestamp: string;
}

const PAGE_SIZE = 50;

const getLevelColor = (level: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (level?.toLowerCase()) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'debug':
      return 'secondary';
    case 'critical':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusColor = (statusCode?: number): 'default' | 'success' | 'error' | 'warning' => {
  if (!statusCode) return 'default';
  if (statusCode >= 200 && statusCode < 300) return 'success';
  if (statusCode >= 400 && statusCode < 500) return 'warning';
  if (statusCode >= 500) return 'error';
  return 'default';
};

const extractEndpoint = (url?: string): string => {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + (urlObj.search || '');
  } catch {
    const match = url.match(/https?:\/\/[^/]+(\/.+)/);
    return match ? match[1] : url;
  }
};

const formatProcessTime = (time?: number): string => {
  if (!time) return '';
  if (time < 1) return `${(time * 1000).toFixed(1)}ms`;
  return `${time.toFixed(3)}s`;
};

const SystemLogsPage: React.FC = () => {
  const [data, setData] = useState<LogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [level, setLevel] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const fetchSystemLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string | number> = { page, page_size: PAGE_SIZE };
      if (level) params.level = level;
      if (source) params.source = source;
      if (search) params.search = search;
      const result = await apiService.getSystemLogs(params);
      setData(result);
    } catch (err: unknown) {
      setError('Failed to load system logs');
      console.error('Error fetching system logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, level, source, search]);

  useEffect(() => {
    fetchSystemLogs();
  }, [fetchSystemLogs]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        System Logs
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select value={level} label="Level" onChange={(e) => setLevel(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="INFO">INFO</MenuItem>
                <MenuItem value="WARNING">WARNING</MenuItem>
                <MenuItem value="ERROR">ERROR</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {data && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  data.logs.map((log, index) => {
                    const details = log.details as Record<string, unknown> | undefined;
                    const isRequestProcessed = log.message === 'Request processed';
                    const endpoint = isRequestProcessed ? extractEndpoint(details?.url as string | undefined) : '';
                    const method = isRequestProcessed ? (details?.method as string | undefined) : '';
                    const statusCode = isRequestProcessed ? (details?.status_code as number | undefined) : undefined;
                    const processTime = isRequestProcessed ? (details?.process_time as number | undefined) : undefined;
                    const isExpanded = expandedRows.has(index);

                    return (
                      <React.Fragment key={index}>
                        <TableRow hover>
                          <TableCell>
                            {details && Object.keys(details).length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newExpanded = new Set(expandedRows);
                                  if (isExpanded) {
                                    newExpanded.delete(index);
                                  } else {
                                    newExpanded.add(index);
                                  }
                                  setExpandedRows(newExpanded);
                                }}
                              >
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {format(new Date(log.timestamp), 'MM/dd HH:mm:ss')}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={log.level} color={getLevelColor(log.level)} />
                          </TableCell>
                          <TableCell>{log.source}</TableCell>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {log.message}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{endpoint || '-'}</TableCell>
                          <TableCell>
                            {method && (
                              <Chip
                                size="small"
                                label={method}
                                color={method === 'GET' ? 'primary' : method === 'POST' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            )}
                            {!method && '-'}
                          </TableCell>
                          <TableCell>
                            {statusCode !== undefined && (
                              <Chip size="small" label={statusCode} color={getStatusColor(statusCode)} />
                            )}
                            {statusCode === undefined && '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {formatProcessTime(processTime) || '-'}
                          </TableCell>
                        </TableRow>
                        {isExpanded && details && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Details:
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      overflow: 'auto',
                                      backgroundColor: 'white',
                                      p: 1,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    {JSON.stringify(details, null, 2)}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data.pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.pagination.total_pages}
                page={data.pagination.page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SystemLogsPage;
